import PropTypes from "prop-types";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "src/@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/@/components/ui/dropdown-menu";
import { Play, Pause, Download, Loader2 } from "lucide-react";
import { useWavesurfer } from "src/hooks/useWavesurfer";
import useSettings from "src/hooks/useSettings";

// Player <audio> nativo, usado quando o áudio vem de uma URL remota (mídia das
// páginas Meta servida pelo S3). O WaveSurfer precisa fazer fetch() do arquivo
// para calcular os peaks da waveform, e o bucket não responde CORS — o fetch é
// bloqueado, o "ready" nunca dispara e o player fica travado em 0:00. O elemento
// <audio> toca por streaming direto, sem passar por CORS.
// Sem waveform aqui, por isso: barra de progresso simples (clicável para buscar).
function useNativeAudio(url, ativo) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!ativo) return undefined;
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onLoaded = () => setDuration(audio.duration);
    const onTime = () => setCurrentTime(audio.currentTime);
    const onEnded = () => { setIsPlaying(false); setCurrentTime(0); };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('durationchange', onLoaded);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    return () => {
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('durationchange', onLoaded);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, [ativo, url]);

  const playPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().catch((e) => console.error('Erro ao tocar áudio:', e));
    else audio.pause();
  }, []);

  const seek = useCallback((ratio) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration)) return;
    audio.currentTime = audio.duration * ratio;
  }, []);

  const setRate = useCallback((rate) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.playbackRate = rate;
    audio.preservesPitch = true;
  }, []);

  return { audioRef, isPlaying, currentTime, duration, playPause, seek, setRate };
}

const AudioComponent = ({ url, isMe, downloaded, downloadAudio, loading, remoteSource = false }) => {
  const { t } = useTranslation('chat');
  const [playbackRate, setPlaybackRate] = useState(1);
  const { themeMode } = useSettings();
  const isDark = themeMode === 'dark';

  const nativo = useNativeAudio(url, remoteSource && downloaded);

  const {
    containerRef,
    wavesurfer,
    isPlaying: wsIsPlaying,
    currentTime: wsCurrentTime,
    duration: wsDuration,
    isReady,
    playPause: wsPlayPause,
    setPlaybackRate: setWSPlaybackRate
  } = useWavesurfer({
    // No modo remoto o WaveSurfer nem é carregado (o fetch dele seria bloqueado)
    url: downloaded && !remoteSource ? url : null,
    // Sem o card branco proprio, a waveform senta direto no balao (verde claro
    // ou verde/cinza escuro no dark) — tons neutros translucidos garantem
    // contraste nos dois fundos.
    waveColor: isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.18)",
    progressColor: "#7C3AED",
    cursorColor: "#7C3AED",
    height: 30,
    barWidth: 2,
    barRadius: 2,
    barGap: 1,
    normalize: true,
    pixelRatio: 1
  });

  const isPlaying = remoteSource ? nativo.isPlaying : wsIsPlaying;
  const currentTime = remoteSource ? nativo.currentTime : wsCurrentTime;
  const duration = remoteSource ? nativo.duration : wsDuration;
  const playPause = remoteSource ? nativo.playPause : wsPlayPause;

  const playbackOptions = [
    { value: "1", label: "1x" },
    { value: "1.5", label: "1.5x" },
    { value: "2", label: "2x" },
    { value: "3", label: "3x" },
  ];

  // Enquanto os metadados não carregam, o browser pode reportar Infinity — sem o
  // isFinite o tempo apareceria como "Infinity:NaN".
  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleDownload = async () => {
    if (!downloadAudio) return;
    try {
      await downloadAudio("audio");
    } catch (error) {
      console.error("Error downloading audio:", error);
    }
  };

  const handleClick = async () => {
    if (!downloaded) {
      await handleDownload();
    } else {
      playPause();
    }
  };

  const handlePlaybackRateChange = (value) => {
    const rate = parseFloat(value);
    setPlaybackRate(rate);
    if (remoteSource) nativo.setRate(rate);
    else setWSPlaybackRate(rate);
  };

  // Clique na barra de progresso busca a posição correspondente (equivalente ao
  // clique na waveform do WaveSurfer).
  const handleSeek = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    nativo.seek(ratio);
  };

  const progresso = Number.isFinite(duration) && duration > 0
    ? Math.min((currentTime / duration) * 100, 100)
    : 0;

  // Se não estiver baixado, mostrar apenas o botão de download.
  // Sem card próprio: o player senta direto no balão (como no WhatsApp) — o
  // card branco aninhado no balão verde criava moldura dupla.
  if (!downloaded) {
    return (
      <div className={`${isMe ? "ml-auto" : "mr-auto"} w-full p-1`}>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleDownload}
            disabled={loading}
            className="flex-shrink-0 w-9 h-9 bg-[#7C3AED] hover:bg-[#6d28d9] rounded-full flex items-center justify-center transition-all shadow-sm shadow-[#7C3AED]/20"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 text-white animate-spin" />
            ) : (
              <Download className="w-4 h-4 text-white" fill="white" />
            )}
          </Button>

          {/* Texto indicativo */}
          <span className="text-[11px] text-gray-500 dark:text-white/60 font-medium">{t('audio.downloadToListen', 'Baixe o áudio para ouvir')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isMe ? "ml-auto" : "mr-auto"} w-full p-1`}>
      <div className="flex items-center gap-2 min-w-0">
        {/* Botão Play/Pause */}
        <Button
          onClick={handleClick}
          disabled={loading}
          className="flex-shrink-0 w-9 h-9 bg-[#7C3AED] hover:bg-[#6d28d9] rounded-full flex items-center justify-center transition-all shadow-sm shadow-[#7C3AED]/20"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 text-white animate-spin" />
          ) : (
            isPlaying ? (
              <Pause className="w-4 h-4 text-white" fill="white" />
            ) : (
              <Play className="w-4 h-4 text-white ml-0.5" fill="white" />
            )
          )}
        </Button>

        {/* Mídia remota (Meta/S3): <audio> nativo + barra de progresso, já que a
            waveform exigiria fetch do arquivo (bloqueado por CORS no bucket).
            min-w-0 + flex-1 (sem max-w) deixa a faixa encolher/crescer com o
            balão — o min-w-[100px]/max-w-[120px] antigo somado aos itens fixos
            passava dos 270px do wrapper e cortava o seletor de velocidade. */}
        {remoteSource ? (
          <>
            <audio ref={nativo.audioRef} src={url} preload="metadata" className="hidden" />
            <div
              onClick={handleSeek}
              className="flex-1 min-w-0 h-8 flex items-center cursor-pointer group"
            >
              <div className="w-full h-1.5 rounded-full bg-black/10 dark:bg-white/20 overflow-hidden">
                <div
                  className="h-full bg-[#7C3AED] rounded-full transition-[width] duration-100"
                  style={{ width: `${progresso}%` }}
                />
              </div>
            </div>
          </>
        ) : (
          /* O container precisa ter altura fixa para não "esticar" */
          <div
            ref={containerRef}
            className="flex-1 min-w-0 h-8"
            style={{ lineHeight: 0 }}
          />
        )}

        {/* Tempo e Velocidade */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <div className="text-[11px] text-gray-500 dark:text-white/60 font-medium min-w-[30px] text-right tabular-nums">
            {isPlaying ? formatTime(currentTime) : formatTime(duration)}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 px-1.5 text-[10px] font-semibold rounded-full bg-black/5 dark:bg-white/10 text-gray-600 dark:text-white/70 hover:bg-black/10 dark:hover:bg-white/20"
              >
                {playbackRate}x
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-16 min-w-0">
              {playbackOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => handlePlaybackRateChange(option.value)}
                  className="text-xs justify-center"
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

AudioComponent.propTypes = {
  url: PropTypes.string,
  isMe: PropTypes.bool.isRequired,
  downloaded: PropTypes.bool,
  downloadAudio: PropTypes.func,
  loading: PropTypes.bool,
  // true para mídia servida por URL remota (páginas Meta): usa <audio> nativo
  // em vez do WaveSurfer, que depende de fetch/CORS.
  remoteSource: PropTypes.bool,
};

export default AudioComponent;
